"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import Image from "next/image";
import { AnimatePresence, motion } from "framer-motion";
import { useQueryClient } from "@tanstack/react-query";

import {
  Modal,
  ModalContent,
  ModalClose,
  ModalTitle,
} from "@/components/ui/modal";
import { Button, SubmitButton } from "@/components/ui/button";
import { formatLongDate } from "@/lib/date/format-long-date";
import { Select, useSelectActions } from "@/components/ui/select";
import { Textarea } from "@/components/ui/input";
import { Icon, type IconName } from "@/components/ui/icon";
import { showToast } from "@/components/ui/sonner";
import { getApiErrorMessage } from "@/lib/api/error";
import { cn } from "@/lib/utils";

import {
  useCancelContext,
  useCancelContact,
  useCancelDiscount,
  useCancelFeedback,
  useCancelPause,
  useConfirmCancel,
  type CancelReasonOption,
} from "../hooks/use-subscription";

const GOLD = "#FFAF53";
/** Solid gold primary CTA. Overrides the `primary` variant's blue. */
const GOLD_BUTTON =
  "border-[#FFAF53] bg-[#FFAF53] text-[#171717] hover:bg-[#ffa53e] active:bg-[#f59e35] font-semibold";

interface CancelFlowProps {
  open: boolean;
  onClose: () => void;
  periodEndDate: string | null;
}

type Step =
  | "loading"
  | "ineligible"
  | "pause"
  | "annual"
  | "reason"
  | "discount"
  | "confirm"
  | "contact"
  | "done";

type DoneKind = "paused" | "canceled" | "discount" | "contact";

const STEP_TRANSITION = { duration: 0.2, ease: [0.4, 0, 0.2, 1] as const };

function formatDate(value: string | null): string {
  return formatLongDate(value) ?? "the end of your billing period";
}

/**
 * Add calendar months to an ISO date, clamping the day to the target month's
 * last valid day. Mirrors the backend `add_months` so the pause preview matches
 * the authoritative dates the server returns on confirm.
 */
function addMonths(iso: string, months: number): string {
  const d = new Date(iso);
  const day = d.getDate();
  d.setDate(1);
  d.setMonth(d.getMonth() + months);
  const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  d.setDate(Math.min(day, lastDay));
  return d.toISOString();
}

function monthsLabel(n: number): string {
  return `${n} ${n === 1 ? "month" : "months"}`;
}

export function CancelFlow({ open, onClose, periodEndDate }: CancelFlowProps) {
  const queryClient = useQueryClient();

  const { data: ctx } = useCancelContext(open);
  const pause = useCancelPause();
  const feedback = useCancelFeedback();
  const discount = useCancelDiscount();
  const contact = useCancelContact();
  const confirmCancel = useConfirmCancel();

  const [step, setStep] = useState<Step>("loading");
  const [months, setMonths] = useState(1);
  const [reasonCode, setReasonCode] = useState<string | null>(null);
  const [reasonText, setReasonText] = useState("");
  const [contactMessage, setContactMessage] = useState("");
  const [resumeAt, setResumeAt] = useState<string | null>(null);
  const [doneKind, setDoneKind] = useState<DoneKind>("canceled");
  const [appliedPercent, setAppliedPercent] = useState<number | null>(null);
  const initRef = useRef(false);

  // Reset transient state every time the modal opens.
  useEffect(() => {
    if (!open) return;
    initRef.current = false;
    setStep("loading");
    setMonths(1);
    setReasonCode(null);
    setReasonText("");
    setContactMessage("");
    setResumeAt(null);
    setAppliedPercent(null);
  }, [open]);

  // Pick the starting step once the context resolves.
  useEffect(() => {
    if (!open || initRef.current || !ctx) return;
    initRef.current = true;
    if (!ctx.eligible) setStep("ineligible");
    else if (ctx.cadence === "annual") setStep("annual");
    else setStep(ctx.can_pause ? "pause" : "reason");
  }, [open, ctx]);

  const busy =
    pause.isPending ||
    feedback.isPending ||
    discount.isPending ||
    contact.isPending ||
    confirmCancel.isPending;

  const discountPercent = appliedPercent ?? ctx?.discount_percent ?? 50;
  const pauseOptions =
    ctx?.pause_months_options && ctx.pause_months_options.length > 0
      ? ctx.pause_months_options
      : [1, 2, 3];

  // Pause preview mirrors the backend: from the upcoming renewal date through
  // renewal + N months. Dates originate from the server (`ctx.period_end`).
  const pauseStart = ctx?.period_end ?? null;
  const revivalDate = useMemo(
    () => addMonths(pauseStart ?? new Date().toISOString(), months),
    [pauseStart, months],
  );

  function invalidateBilling() {
    queryClient.invalidateQueries({ queryKey: ["billing", "subscription"] });
    queryClient.invalidateQueries({ queryKey: ["billing", "org-status"] });
    queryClient.invalidateQueries({ queryKey: ["billing", "seats"] });
    queryClient.invalidateQueries({ queryKey: ["billing", "credits"] });
  }

  async function handlePause() {
    try {
      const res = await pause.mutateAsync(months);
      invalidateBilling();
      setResumeAt(res.resume_at);
      setDoneKind("paused");
      setStep("done");
    } catch (err) {
      showToast.error("Couldn't pause subscription", {
        description: getApiErrorMessage(err, "Please try again in a moment."),
      });
    }
  }

  async function handleSubmitReason() {
    if (!reasonCode) return;
    const reason_text = reasonCode === "other" ? reasonText.trim() : null;
    try {
      const res = await feedback.mutateAsync({
        reason_code: reasonCode,
        reason_text,
      });
      setStep(res.discount_available ? "discount" : "confirm");
    } catch (err) {
      showToast.error("Something went wrong", {
        description: getApiErrorMessage(err, "Please try again in a moment."),
      });
    }
  }

  async function handleAcceptDiscount() {
    try {
      const res = await discount.mutateAsync();
      invalidateBilling();
      setAppliedPercent(res.percent);
      setDoneKind("discount");
      setStep("done");
    } catch (err) {
      showToast.error("Couldn't apply the discount", {
        description: getApiErrorMessage(err, "Please try again in a moment."),
      });
    }
  }

  async function handleConfirmCancel() {
    try {
      await confirmCancel.mutateAsync(
        reasonCode
          ? {
              reason_code: reasonCode,
              reason_text: reasonCode === "other" ? reasonText.trim() : null,
            }
          : undefined,
      );
      setDoneKind("canceled");
      setStep("done");
    } catch (err) {
      showToast.error("Couldn't cancel subscription", {
        description: getApiErrorMessage(err, "Please try again in a moment."),
      });
    }
  }

  async function handleSubmitContact() {
    try {
      await contact.mutateAsync({
        reason_code: reasonCode,
        message: contactMessage.trim() || null,
      });
      setDoneKind("contact");
      setStep("done");
    } catch (err) {
      showToast.error("Couldn't send your request", {
        description: getApiErrorMessage(err, "Please try again in a moment."),
      });
    }
  }

  const header = headerForStep(step, doneKind);

  return (
    <Modal
      open={open}
      onOpenChange={(o) => !o && onClose()}
      dismissible={!busy}
    >
      <ModalContent
        size="md"
        className="overflow-visible border-transparent bg-transparent p-0 shadow-none sm:max-w-[480px]"
      >
        <GoldTab title={header.title} icon={header.icon} />

        <div className="overflow-hidden rounded-xl border border-border bg-surface-elevated shadow-[var(--modal-shadow)]">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 8 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -8 }}
              transition={STEP_TRANSITION}
            >
              {step === "loading" && <LoadingState />}

              {step === "ineligible" && (
                <>
                  <div className="px-6 pb-6 pt-6">
                    <p className="text-sm leading-relaxed text-white/70">
                      {ctx?.reason_ineligible ??
                        "This plan is managed by your account team."}{" "}
                      Reach out to your account manager to make changes.
                    </p>
                  </div>
                  <Footer>
                    <span />
                    <Button variant="secondary" size="sm" onClick={onClose}>
                      Done
                    </Button>
                  </Footer>
                </>
              )}

              {step === "pause" && (
                <>
                  <div className="px-6 pb-5 pt-6">
                    <h2 className="text-2xl font-semibold tracking-[-0.5px] text-white">
                      Let&apos;s hit pause.
                    </h2>
                    <p className="mt-2 text-sm leading-relaxed text-white/60">
                      Pause instead of canceling and keep your candidate data,
                      email templates, seats, and searches. Come back whenever
                      you&apos;re ready.
                    </p>

                    <div className="mt-5 flex flex-col gap-3">
                      <MonthsSelect
                        months={months}
                        options={pauseOptions}
                        onChange={setMonths}
                        disabled={busy}
                      />
                      <SubmitButton
                        type="button"
                        variant="primary"
                        size="lg"
                        loading={pause.isPending}
                        loadingLabel="Pausing"
                        disabled={busy}
                        onClick={handlePause}
                        className={cn("w-full", GOLD_BUTTON)}
                      >
                        Pause Subscription
                      </SubmitButton>
                    </div>

                    <div className="mt-4 border-l-2 border-[#FFAF53]/40 pl-3">
                      <p className="text-sm text-white/80">
                        Your subscription will renew on{" "}
                        <span className="font-medium text-white">
                          {formatDate(revivalDate)}
                        </span>
                      </p>
                      <p className="mt-0.5 text-[13px] text-white/50">
                        Your subscription will be paused from{" "}
                        {formatDate(pauseStart)} until {formatDate(revivalDate)}
                      </p>
                    </div>
                  </div>
                  <Footer>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={onClose}
                      disabled={busy}
                    >
                      Go Back
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setStep("reason")}
                      disabled={busy}
                    >
                      Decline Offer
                    </Button>
                  </Footer>
                </>
              )}

              {step === "annual" && (
                <>
                  <div className="px-6 pb-5 pt-6">
                    <h2 className="text-2xl font-semibold tracking-[-0.5px] text-white">
                      Before you cancel.
                    </h2>
                    <p className="mt-2 text-sm leading-relaxed text-white/60">
                      Your plan is billed annually, so it can&apos;t be paused
                      or auto-discounted. Our team can put together a custom
                      offer to keep you on. Want us to reach out?
                    </p>
                    <div className="mt-5">
                      <SubmitButton
                        type="button"
                        variant="primary"
                        size="lg"
                        disabled={busy}
                        onClick={() => setStep("contact")}
                        className={cn("w-full", GOLD_BUTTON)}
                      >
                        Request a custom discount
                      </SubmitButton>
                    </div>
                  </div>
                  <Footer>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={onClose}
                      disabled={busy}
                    >
                      Go Back
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setStep("reason")}
                      disabled={busy}
                    >
                      Decline Offer
                    </Button>
                  </Footer>
                </>
              )}

              {step === "reason" && (
                <>
                  <div className="px-6 pb-5 pt-6">
                    <h2 className="text-xl font-semibold tracking-[-0.4px] text-white">
                      What&apos;s the reason?
                    </h2>
                    <p className="mt-1.5 text-sm text-white/60">
                      This helps us improve. It won&apos;t change your
                      cancellation.
                    </p>
                    <div className="mt-4 flex flex-col gap-2">
                      {(ctx?.reasons ?? []).map((option) => (
                        <ReasonRow
                          key={option.code}
                          option={option}
                          selected={reasonCode === option.code}
                          onSelect={() => setReasonCode(option.code)}
                        />
                      ))}
                    </div>
                    {reasonCode === "other" && (
                      <Textarea
                        variant="modal"
                        value={reasonText}
                        onChange={(e) => setReasonText(e.target.value)}
                        placeholder="Tell us more…"
                        maxLength={1000}
                        className="mt-3 min-h-20 w-full"
                      />
                    )}
                  </div>
                  <Footer>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        setStep(ctx?.cadence === "annual" ? "annual" : "pause")
                      }
                      disabled={busy}
                    >
                      Go Back
                    </Button>
                    <SubmitButton
                      type="button"
                      variant="primary"
                      size="sm"
                      loading={feedback.isPending}
                      disabled={
                        busy ||
                        !reasonCode ||
                        (reasonCode === "other" && !reasonText.trim())
                      }
                      onClick={handleSubmitReason}
                      className={GOLD_BUTTON}
                    >
                      Continue
                    </SubmitButton>
                  </Footer>
                </>
              )}

              {step === "discount" && (
                <>
                  <div className="px-6 pb-5 pt-6">
                    <div className="flex size-11 items-center justify-center rounded-xl bg-[#FFAF53]">
                      <Image
                        src="/favicon.svg"
                        alt="Neuroscale"
                        width={24}
                        height={20}
                        className="h-5 w-auto brightness-0"
                      />
                    </div>
                    <h2 className="mt-4 text-2xl font-semibold tracking-[-0.5px] text-white">
                      Stay with us, {discountPercent}% off your next month.
                    </h2>
                    <p className="mt-2 text-sm leading-relaxed text-white/60">
                      Your searches, candidates, and team stay exactly where you
                      left them. Keep your momentum and we&apos;ll take{" "}
                      {discountPercent}% off your next invoice, applied
                      automatically. No commitment, cancel anytime.
                    </p>
                  </div>
                  <Footer>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setStep("confirm")}
                      disabled={busy}
                    >
                      No thanks
                    </Button>
                    <SubmitButton
                      type="button"
                      variant="primary"
                      size="sm"
                      loading={discount.isPending}
                      disabled={busy}
                      onClick={handleAcceptDiscount}
                      className={GOLD_BUTTON}
                    >
                      Apply {discountPercent}% off
                    </SubmitButton>
                  </Footer>
                </>
              )}

              {step === "confirm" && (
                <>
                  <div className="px-6 pb-5 pt-6">
                    <p className="text-sm leading-relaxed text-white/80">
                      You&apos;ll keep full access to your plan, seats, and
                      remaining credits until{" "}
                      <span className="font-medium text-white">
                        {formatDate(periodEndDate)}
                      </span>
                      .
                    </p>
                    <div className="mt-3 rounded-lg border border-red-400/20 bg-red-400/6 px-3.5 py-3">
                      <p className="text-sm leading-relaxed text-red-300/90">
                        After that, your organization will be suspended and
                        everyone will lose access until you reactivate. We
                        cannot guarantee any of your data will be retained once
                        your subscription ends.
                      </p>
                    </div>
                  </div>
                  <Footer>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={onClose}
                      disabled={busy}
                    >
                      Keep my plan
                    </Button>
                    <SubmitButton
                      type="button"
                      variant="destructive"
                      size="sm"
                      loading={confirmCancel.isPending}
                      disabled={busy}
                      onClick={handleConfirmCancel}
                    >
                      Cancel subscription
                    </SubmitButton>
                  </Footer>
                </>
              )}

              {step === "contact" && (
                <>
                  <div className="px-6 pb-5 pt-6">
                    <h2 className="text-xl font-semibold tracking-[-0.4px] text-white">
                      Tell us what you need.
                    </h2>
                    <p className="mt-1.5 text-sm leading-relaxed text-white/60">
                      Share what would make you stay and our team will follow up
                      with a custom offer.
                    </p>
                    <Textarea
                      variant="modal"
                      value={contactMessage}
                      onChange={(e) => setContactMessage(e.target.value)}
                      placeholder="What would make you stay? (optional)"
                      maxLength={2000}
                      className="mt-4 min-h-24 w-full"
                    />
                  </div>
                  <Footer>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setStep("annual")}
                      disabled={busy}
                    >
                      Go Back
                    </Button>
                    <SubmitButton
                      type="button"
                      variant="primary"
                      size="sm"
                      loading={contact.isPending}
                      disabled={busy}
                      onClick={handleSubmitContact}
                      className={GOLD_BUTTON}
                    >
                      Submit request
                    </SubmitButton>
                  </Footer>
                </>
              )}

              {step === "done" && (
                <>
                  <DoneState
                    kind={doneKind}
                    resumeAt={resumeAt}
                    periodEndDate={periodEndDate}
                    percent={discountPercent}
                  />
                  <Footer>
                    <span />
                    <Button variant="secondary" size="sm" onClick={onClose}>
                      Done
                    </Button>
                  </Footer>
                </>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </ModalContent>
    </Modal>
  );
}

function headerForStep(
  step: Step,
  doneKind: DoneKind,
): { title: string; icon: IconName } {
  switch (step) {
    case "pause":
      return { title: "Subscription Pause", icon: "IconPause" };
    case "annual":
    case "contact":
      return { title: "Custom Discount", icon: "IconSparkle" };
    case "reason":
      return { title: "Before You Go", icon: "IconHeart" };
    case "discount":
      return { title: "A Parting Gift", icon: "IconTag" };
    case "confirm":
      return { title: "Cancel Subscription", icon: "IconExclamationTriangle" };
    case "done":
      if (doneKind === "paused")
        return { title: "Subscription Paused", icon: "IconPause" };
      if (doneKind === "discount")
        return { title: "Discount Applied", icon: "IconTag" };
      if (doneKind === "contact")
        return { title: "Request Received", icon: "IconCheckmark1" };
      return { title: "Subscription Canceled", icon: "IconCheckmark1" };
    case "ineligible":
      return { title: "Manage Subscription", icon: "IconHeart" };
    default:
      return { title: "Manage Subscription", icon: "IconHeart" };
  }
}

/**
 * Solid-gold folder tab that sits above the modal card: rounded top, ~80%
 * width, centered, so the dark backdrop shows on its shoulders.
 */
function GoldTab({ title, icon }: { title: string; icon: IconName }) {
  return (
    <div className="mx-auto flex h-9 w-[93%] items-center justify-between gap-2 rounded-t-xl bg-[#FFAF53] pl-3.5 pr-2">
      <div className="flex min-w-0 items-center gap-1.5">
        <Icon name={icon} size={14} className="shrink-0 text-[#171717]" />
        <ModalTitle className="truncate text-[13px] font-semibold text-[#171717]">
          {title}
        </ModalTitle>
      </div>
      <ModalClose asChild>
        <button
          type="button"
          aria-label="Close"
          className="flex size-6 items-center justify-center rounded-md text-[#171717]/70 transition-colors hover:bg-black/10 hover:text-[#171717]"
        >
          <Icon name="IconCrossMedium" size={14} />
        </button>
      </ModalClose>
    </div>
  );
}

function Footer({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 border-t border-white/10 px-6 py-4">
      {children}
    </div>
  );
}

function MonthsSelect({
  months,
  options,
  onChange,
  disabled,
}: {
  months: number;
  options: number[];
  onChange: (value: number) => void;
  disabled?: boolean;
}) {
  return (
    <Select.Root modal>
      <Select.Trigger disabled={disabled} className="w-full justify-between">
        <span className="flex-1 text-left text-sm text-white">
          {monthsLabel(months)}
        </span>
        <Select.Icon />
      </Select.Trigger>
      <Select.Content>
        <MonthsList months={months} options={options} onChange={onChange} />
      </Select.Content>
    </Select.Root>
  );
}

function MonthsList({
  months,
  options,
  onChange,
}: {
  months: number;
  options: number[];
  onChange: (value: number) => void;
}) {
  const { setOpen } = useSelectActions();
  return (
    <Select.List>
      {options.map((value) => (
        <Select.Item
          key={value}
          selected={value === months}
          onSelect={() => {
            onChange(value);
            setOpen(false);
          }}
        >
          <Select.ItemLabel selected={value === months}>
            {monthsLabel(value)}
          </Select.ItemLabel>
          {value === months ? <Select.ItemCheck /> : null}
        </Select.Item>
      ))}
    </Select.List>
  );
}

function ReasonRow({
  option,
  selected,
  onSelect,
}: {
  option: CancelReasonOption;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "flex items-center gap-2.5 rounded-lg border px-3.5 py-2.5 text-left text-sm transition-colors cursor-pointer",
        selected
          ? "border-[#FFAF53]/60 bg-[#FFAF53]/8 text-white"
          : "border-white/10 bg-white/2 text-white/80 hover:bg-white/4",
      )}
    >
      <span
        className={cn(
          "flex size-4 shrink-0 items-center justify-center rounded-full border transition-colors",
          selected ? "border-[#FFAF53] bg-[#FFAF53]" : "border-white/25",
        )}
      >
        {selected && <span className="size-1.5 rounded-full bg-[#171717]" />}
      </span>
      {option.label}
    </button>
  );
}

function LoadingState() {
  return (
    <div className="flex flex-col gap-3 px-6 pb-6 pt-6">
      <div className="h-7 w-1/2 animate-pulse rounded bg-white/6" />
      <div className="h-4 w-full animate-pulse rounded bg-white/4" />
      <div className="h-4 w-2/3 animate-pulse rounded bg-white/4" />
      <div className="mt-2 h-10 w-full animate-pulse rounded-lg bg-white/5" />
    </div>
  );
}

function DoneState({
  kind,
  resumeAt,
  periodEndDate,
  percent,
}: {
  kind: DoneKind;
  resumeAt: string | null;
  periodEndDate: string | null;
  percent: number;
}) {
  const config: Record<
    DoneKind,
    {
      icon: IconName;
      tint: string;
      iconColor: string;
      title: string;
      body: React.ReactNode;
    }
  > = {
    paused: {
      icon: "IconPause",
      tint: "bg-[#FFAF53]/15",
      iconColor: GOLD,
      title: "Your subscription is paused",
      body: (
        <>
          We&apos;ll automatically resume your plan on{" "}
          <span className="font-medium text-white">{formatDate(resumeAt)}</span>
          . Your data stays exactly where you left it.
        </>
      ),
    },
    discount: {
      icon: "IconTag",
      tint: "bg-[#FFAF53]/15",
      iconColor: GOLD,
      title: `${percent}% off applied`,
      body: (
        <>
          Your {percent}% discount will be applied to your next invoice. Thanks
          for sticking with us.
        </>
      ),
    },
    contact: {
      icon: "IconCheckmark1",
      tint: "bg-[#FFAF53]/15",
      iconColor: GOLD,
      title: "Request received",
      body: (
        <>
          Our team will reach out shortly with a custom offer. Your plan stays
          active in the meantime.
        </>
      ),
    },
    canceled: {
      icon: "IconCheckmark1",
      tint: "bg-white/6",
      iconColor: "rgba(255,255,255,0.8)",
      title: "Your subscription is canceled",
      body: (
        <>
          You&apos;ll keep full access until{" "}
          <span className="font-medium text-white">
            {formatDate(periodEndDate)}
          </span>
          . Change your mind? Hit &ldquo;Keep my plan&rdquo; anytime before
          then.
        </>
      ),
    },
  };

  const c = config[kind];

  return (
    <div className="px-6 pb-2 pt-6">
      <div className="flex flex-col items-center py-2 text-center">
        <div
          className={cn(
            "mb-4 flex size-12 items-center justify-center rounded-full",
            c.tint,
          )}
        >
          <Icon name={c.icon} size={22} style={{ color: c.iconColor }} />
        </div>
        <h3 className="text-base font-semibold tracking-[-0.16px] text-white">
          {c.title}
        </h3>
        <p className="mt-1.5 max-w-[340px] text-sm leading-relaxed text-white/70">
          {c.body}
        </p>
      </div>
    </div>
  );
}
